import React, { useState, useEffect, useCallback } from "react";
import S from "./adminStyles";
import { exportToCSV, printTable } from "../../utils/exportUtils";

function ConfirmDeleteUserDialog({ email, onConfirm, onCancel }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div style={{
        background: "#fff", borderRadius: "8px", padding: "24px", maxWidth: "400px",
        width: "90%", boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
      }}>
        <h3 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#1e293b" }}>Delete Admin User?</h3>
        <p style={{ margin: "0 0 20px 0", fontSize: "14px", color: "#475569" }}>
          <strong>{email}</strong> will be permanently deleted. This action cannot be undone.
        </p>
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <button
            style={{ padding: "8px 16px", borderRadius: "4px", border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: "13px" }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            style={{ padding: "8px 16px", borderRadius: "4px", border: "none", background: "#dc2626", color: "#fff", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
            onClick={onConfirm}
          >
            Delete User
          </button>
        </div>
      </div>
    </div>
  );
}

const ROLE_OPTIONS = [
  { value: "", label: "(none)" },
  { value: "system", label: "system" },
  { value: "counselor", label: "counselor" },
  { value: "operations", label: "operations" },
];

export default function AdminUsersTab({ auth }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [updatingUid, setUpdatingUid] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newRole, setNewRole] = useState("counselor");
  const [creating, setCreating] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [deletingUid, setDeletingUid] = useState(null);
  const [togglingUid, setTogglingUid] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);

  const handleCancelCreate = useCallback(() => setShowCreateForm(false), []);

  const loadUsers = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const currentUser = auth?.currentUser;
      if (!currentUser) {
        throw new Error("Not authenticated.");
      }
      const token = await currentUser.getIdToken();
      const res = await fetch("/api/admin/users", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Unable to load users (${res.status}).`);
      }
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error("AdminUsersTab load error:", err);
      setError(err?.message || "Unable to load users.");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(""), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!showCreateForm) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleCancelCreate();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showCreateForm, handleCancelCreate]);

  const updateUserRole = async (uid, role) => {
    setError("");
    setSuccess("");
    setUpdatingUid(uid);
    try {
      const currentUser = auth?.currentUser;
      if (!currentUser) {
        throw new Error("Not authenticated.");
      }
      const token = await currentUser.getIdToken();
      const res = await fetch(`/api/admin/users/${uid}/role`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Unable to update role (${res.status}).`);
      }
      setUsers((prev) =>
        prev.map((user) =>
          user.uid === uid ? { ...user, admin_role: role } : user,
        ),
      );
      setSuccess("Role updated successfully.");
    } catch (err) {
      console.error("AdminUsersTab update error:", err);
      setError(err?.message || "Unable to update role.");
    } finally {
      setUpdatingUid(null);
    }
  };

  const createUser = async () => {
    setError("");
    setSuccess("");
    setTempPassword("");
    setCreating(true);
    try {
      const currentUser = auth?.currentUser;
      if (!currentUser) throw new Error("Not authenticated.");
      const token = await currentUser.getIdToken();
      const res = await fetch("/api/admin/users", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: newEmail, displayName: newDisplayName, role: newRole }),
      });
      const body = await res.json().catch(() => null);
      if (res.status === 409) {
        throw new Error("An account with this email already exists.");
      }
      if (!res.ok) {
        throw new Error(body?.error || `Failed to create user (${res.status}).`);
      }
      setTempPassword(body.tempPassword);
      await loadUsers();
      setNewEmail("");
      setNewDisplayName("");
      setNewRole("counselor");
      setShowCreateForm(false);
      setSuccess(`User created. Temporary password: ${body.tempPassword}`);
    } catch (err) {
      console.error("AdminUsersTab create error:", err);
      setError(err?.message || "Unable to create user.");
    } finally {
      setCreating(false);
    }
  };

  const toggleDisabled = async (uid, currentDisabledState) => {
    setError("");
    setSuccess("");
    setTogglingUid(uid);
    try {
      const currentUser = auth?.currentUser;
      if (!currentUser) throw new Error("Not authenticated.");
      const token = await currentUser.getIdToken();
      const res = await fetch(`/api/admin/users/${uid}/disable`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ disabled: !currentDisabledState }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(body?.error || `Failed to update user (${res.status}).`);
      }
      setUsers((prev) =>
        prev.map((u) => u.uid === uid ? { ...u, disabled: !currentDisabledState } : u),
      );
      setSuccess(`User ${!currentDisabledState ? "disabled" : "enabled"} successfully.`);
    } catch (err) {
      console.error("AdminUsersTab toggle error:", err);
      setError(err?.message || "Unable to update user.");
    } finally {
      setTogglingUid(null);
    }
  };

  const deleteUser = async (uid) => {
    setError("");
    setSuccess("");
    setDeletingUid(uid);
    try {
      const currentUser = auth?.currentUser;
      if (!currentUser) throw new Error("Not authenticated.");
      const token = await currentUser.getIdToken();
      const res = await fetch(`/api/admin/users/${uid}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(body?.error || `Failed to delete user (${res.status}).`);
      }
      setUsers((prev) => prev.filter((u) => u.uid !== uid));
      setSuccess("User deleted successfully.");
    } catch (err) {
      console.error("AdminUsersTab delete error:", err);
      setError(err?.message || "Unable to delete user.");
    } finally {
      setDeletingUid(null);
    }
  };

  const USERS_COLS = [
    { key: "email", label: "Email" },
    { key: "displayName", label: "Display Name" },
    { key: "admin_role", label: "Role" },
    { key: "disabled", label: "Disabled" },
  ];

  const exportUsersCSV = () => {
    const today = new Date().toISOString().split("T")[0];
    exportToCSV(`raider-depot-users-${today}.csv`, users, USERS_COLS);
  };

  const printUsers = () => {
    printTable("Users", `${users.length} users`, USERS_COLS, users);
  };

  const currentUid = auth?.currentUser?.uid;

  return (
    <div>
      <div style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#ffffff", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
        <div style={S.flexRowBetween}>
          <h2 style={S.cardTitle}>Admin Users</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btnSecondary} onClick={exportUsersCSV} disabled={users.length === 0}>
              Export CSV
            </button>
            <button style={S.btnSecondary} onClick={printUsers} disabled={users.length === 0}>
              Print
            </button>
            <button style={S.btnSecondary} onClick={loadUsers}>
              Refresh
            </button>
            <button
              style={{ ...S.btnSecondary, borderColor: "#3b82f6", color: "#3b82f6" }}
              onClick={() => { setShowCreateForm(true); setTempPassword(""); }}
            >
              + Create User
            </button>
          </div>
        </div>
        {!loading && users.length > 0 && (
          <table style={{ ...S.table, tableLayout: "fixed" }}>
            <thead style={{ backgroundColor: "#ffffff" }}>
              <tr>
                <th style={{ ...S.th, width: "26%" }}>Email</th>
                <th style={{ ...S.th, width: "22%" }}>Display Name</th>
                <th style={{ ...S.th, width: "12%" }}>Role</th>
                <th style={{ ...S.th, width: "18%" }}>Last Sign In</th>
                <th style={{ ...S.th, width: "10%" }}>Status</th>
                <th style={{ ...S.th, width: "12%" }}>Actions</th>
              </tr>
            </thead>
          </table>
        )}
      </div>

      {showCreateForm && (
        <div style={{ border: "2px solid #3b82f6", borderRadius: 8, padding: 16, marginBottom: 16, backgroundColor: "#eff6ff" }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, color: "#1e40af" }}>Create Admin User</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              type="email"
              placeholder="Email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              style={{ ...S.select, width: "100%", boxSizing: "border-box" }}
            />
            <input
              type="text"
              placeholder="Display name (optional)"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              style={{ ...S.select, width: "100%", boxSizing: "border-box" }}
            />
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              style={{ ...S.select, width: "100%" }}
            >
              <option value="counselor">counselor</option>
              <option value="operations">operations</option>
              <option value="system">system</option>
            </select>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                style={{
                  background: "#1A3B6E",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: creating || !newEmail ? "not-allowed" : "pointer",
                  opacity: creating || !newEmail ? 0.6 : 1,
                }}
                onClick={createUser}
                disabled={creating || !newEmail}
              >
                {creating ? "Creating..." : "Create"}
              </button>
              <button
                style={S.btnSecondary}
                onClick={handleCancelCreate}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {tempPassword && (
        <div style={{ border: "1px solid #34d399", borderRadius: 8, padding: 12, marginBottom: 16, backgroundColor: "#ecfdf5", color: "#166534" }}>
          <strong>Temporary password:</strong> {tempPassword}
          <br />
          <span style={{ fontSize: 12 }}>Share this password securely with the user. They should change it on first login.</span>
        </div>
      )}

      {error ? (
        <div
          style={{
            ...S.alertBanner,
            backgroundColor: "#fee2e2",
            borderColor: "#fecaca",
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      ) : null}

      {success ? (
        <div
          style={{
            ...S.alertBanner,
            backgroundColor: "#ecfdf5",
            borderColor: "#34d399",
            color: "#166534",
          }}
        >
          {success}
        </div>
      ) : null}

      <div style={{ backgroundColor: "#fff", padding: "20px", marginBottom: "16px" }}>
        {loading ? (
          <p style={{ color: "#64748b" }}>Loading users...</p>
        ) : users.length === 0 ? (
          <p style={{ color: "#94a3b8", fontSize: "13px" }}>
            No users found.
          </p>
        ) : (
          <table style={{ ...S.table, tableLayout: "fixed" }}>
            <thead style={{ visibility: "hidden" }}>
              <tr>
                <th style={{ ...S.th, width: "26%" }}>Email</th>
                <th style={{ ...S.th, width: "22%" }}>Display Name</th>
                <th style={{ ...S.th, width: "12%" }}>Role</th>
                <th style={{ ...S.th, width: "18%" }}>Last Sign In</th>
                <th style={{ ...S.th, width: "10%" }}>Status</th>
                <th style={{ ...S.th, width: "12%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const status = user.disabled ? "disabled" : "active";
                const isSelf = currentUid === user.uid;
                const isMasterAdmin = (u) => u.email === 'admin@cobbfamilyfoundation.org';
                return (
                  <tr key={user.uid}>
                    <td style={S.td}>
                      {user.email || "—"}
                    </td>
                    <td style={S.td}>
                      {user.displayName || "—"}
                    </td>
                    <td style={S.td}>
                      <select
                        style={{ ...S.select, width: "100%" }}
                        value={user.admin_role || ""}
                        disabled={isSelf || updatingUid === user.uid}
                        onChange={(e) => updateUserRole(user.uid, e.target.value)}
                      >
                        {ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={S.td}>
                      {user.lastSignInTime ? (
                        <span style={{ color: "#64748b", fontSize: "12px" }}>
                          {new Date(user.lastSignInTime).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                      ) : (
                        <span style={{ color: "#94a3b8", fontSize: "12px" }}>
                          Unknown
                        </span>
                      )}
                    </td>
                    <td style={S.td}>
                      <span
                        style={S.badge(status === "active" ? "#16a34a" : "#dc2626")}
                      >
                        {status}
                      </span>
                    </td>
                    <td style={S.td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {isMasterAdmin(user) ? (
                          <span style={{
                            fontSize: 11,
                            color: "#6b7280",
                            backgroundColor: "#f3f4f6",
                            border: "1px solid #e5e7eb",
                            borderRadius: 4,
                            padding: "2px 8px",
                          }}>
                            Protected
                          </span>
                        ) : (
                          <>
                            <button
                              style={{
                                background: user.disabled ? "#16a34a" : "#d97706",
                                color: "#fff",
                                border: "none",
                                borderRadius: 4,
                                padding: "3px 8px",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: isSelf ? "not-allowed" : "pointer",
                                opacity: isSelf || togglingUid === user.uid ? 0.5 : 1,
                              }}
                              disabled={isSelf || togglingUid === user.uid}
                              onClick={() => toggleDisabled(user.uid, user.disabled)}
                            >
                              {user.disabled ? "Enable" : "Disable"}
                            </button>
                            <button
                              style={{
                                background: "#dc2626",
                                color: "#fff",
                                border: "none",
                                borderRadius: 4,
                                padding: "3px 8px",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: isSelf ? "not-allowed" : "pointer",
                                opacity: isSelf || deletingUid === user.uid ? 0.5 : 1,
                              }}
                              disabled={isSelf || deletingUid === user.uid}
                              onClick={() => setConfirmDeleteUser({ uid: user.uid, email: user.email })}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {confirmDeleteUser && (
        <ConfirmDeleteUserDialog
          email={confirmDeleteUser.email}
          onConfirm={() => {
            deleteUser(confirmDeleteUser.uid);
            setConfirmDeleteUser(null);
          }}
          onCancel={() => setConfirmDeleteUser(null)}
        />
      )}
    </div>
  );
}
